import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { onCLS, onINP, onLCP } from 'web-vitals';
import { MantineProvider } from '@mantine/core';
import { Notifications, notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { PageContextProvider } from './providers/PageContextProvider';
import { RootErrorBoundary } from './components/RootErrorBoundary';
import { AppUpdateOnNavigation } from './components/AppUpdateOnNavigation';
import { theme } from './theme';
import { useAuthStore } from './stores/auth';
import { useSettingsStore } from './stores/settings';
import { useCustomerStore } from './stores/customerStore';
import { initOfflineDB } from './lib/offlineDB';
import { registerServiceWorker } from './lib/appUpdate';
import { KIAN_USAGE_EVENT } from './lib/api/http';
import { formatKianUsage, kianUsageFrom } from './components/KianUsageLine';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  });
  const reportVital = (name: string, value: number) => {
    Sentry.addBreadcrumb({
      category: 'web-vitals',
      message: `${name}: ${Math.round(value)}`,
      level: 'info',
    });
  };
  onLCP((m) => reportVital('LCP', m.value));
  onINP((m) => reportVital('INP', m.value));
  onCLS((m) => reportVital('CLS', m.value));
}

/** API401 (z. B. Sitzung widerrufen / Device-Limit): Store leeren und zur Anmeldung. */
function registerPortalSessionInvalidHandler() {
  if (typeof window === 'undefined') return;
  window.addEventListener('portal:session-invalid', () => {
    useAuthStore.getState().clearSession();
    const path = window.location.pathname;
    // Nicht auf das Techniker-Login umleiten, wenn der Nutzer im Kundenportal
    // ist – dort gilt die customer-portal:session-invalid-Behandlung.
    // Achtung: präzise auf "/customer/" prüfen (mit Slash). Das Kundenportal
    // liegt unter /customer/*; Techniker-Seiten wie /customer-hub und
    // /customer-approve dürfen weiterhin aufs Techniker-Login umleiten.
    const isCustomerPortal = path === '/customer' || path.startsWith('/customer/');
    if (!path.startsWith('/login') && !isCustomerPortal) {
      window.location.replace('/login?reason=session_ended');
    }
  });
}
registerPortalSessionInvalidHandler();

/** Allgemeiner KIAN-Hinweis: welches Modell, wie viele Tokens, was der Aufruf gekostet hat. */
function registerKianUsageNotice() {
  if (typeof window === 'undefined') return;
  window.addEventListener(KIAN_USAGE_EVENT, (event) => {
    const usage = kianUsageFrom((event as CustomEvent<{ usage?: unknown }>).detail?.usage);
    if (!usage) return;
    notifications.show({
      title: 'KIAN',
      message: formatKianUsage(usage),
      color: 'grape',
      autoClose: 6000,
    });
  });
}
registerKianUsageNotice();

function registerCustomerPortalSessionInvalidHandler() {
  if (typeof window === 'undefined') return;
  window.addEventListener('customer-portal:session-invalid', () => {
    useCustomerStore.getState().clear();
    const path = window.location.pathname;
    if (path.startsWith('/customer') && !path.startsWith('/customer/login')) {
      window.location.replace('/customer/login?reason=session_ended');
    }
  });
}
registerCustomerPortalSessionInvalidHandler();

/** Initialisiere Offline-Datenbank (IndexedDB) */
void initOfflineDB().catch((err) => {
  console.error('[main] Failed to initialize offline DB:', err);
});

/** Service Worker und neue Versionen, ohne dass ein Reload Eingaben verwirft (lib/appUpdate). */
void registerServiceWorker();

/** Nach Rehydration: LLM-Status laden, falls Sitzung aus persist noch aktiv ist (ergänzt checkAuth in App). */
useAuthStore.persist.onFinishHydration(() => {
  if (useAuthStore.getState().isAuthenticated) {
    void useSettingsStore.getState().loadLlmKeyStatus();
  }
});

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  document.body.innerHTML =
    '<div style="padding:24px;font-family:sans-serif;background:#121212;color:#fff;">' +
    '<strong>Kein #root-Element</strong> — prüfen Sie <code>index.html</code> (SPA-Deploy).</div>';
} else {
  ReactDOM.createRoot(rootEl).render(
    <RootErrorBoundary>
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          {/* forceColorScheme statt defaultColorScheme: das Technikerportal hat
              keinen Umschalter, und seine Flächen sind hart dunkel gesetzt.
              Mit `default` entschied ein alter Wert in localStorage
              (mantine-color-scheme-value) — stand dort `light`, lief die
              gesamte Oberfläche dauerhaft mit hellen Text-Tokens auf dunklen
              Flächen: schwarze Schrift auf Dunkelgrau, 1,35:1 beim Zeigen. */}
          <MantineProvider theme={theme} forceColorScheme="dark">
            {/* Über allen Overlays (Cockpit 500/650, Kunden-Modal 600, Schreib-Modals 700,
                Comboboxen 1100) — sonst poppen Fehlermeldungen unsichtbar dahinter auf. */}
            <Notifications position="top-right" zIndex={1400} />
            <BrowserRouter>
              <AppUpdateOnNavigation />
              <PageContextProvider>
                <App />
              </PageContextProvider>
            </BrowserRouter>
          </MantineProvider>
        </QueryClientProvider>
      </React.StrictMode>
    </RootErrorBoundary>
  );
}

